/**
 * Animations.js
 *
 * GSAP + ScrollTrigger setup.
 * This module only CONFIGURES the library.
 * Actual animations are added here as reusable factories, then called from app.js.
 *
 * Tree-shaking note: only imported GSAP members are bundled.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { prefersReducedMotion } from '../utilities/Utilities.js';

// Register plugins once, globally
gsap.registerPlugin(ScrollTrigger);

// Global GSAP defaults
gsap.defaults({
  ease: 'power3.out',
  duration: 0.7,
});

// ScrollTrigger defaults — smooth scrub, generous start offset
ScrollTrigger.defaults({
  start: 'top 88%',
  toggleActions: 'play none none none',
});

/**
 * Fade-up stagger — apply to a group of elements (NodeList or Array).
 * Usage: fadeUpStagger(document.querySelectorAll('[data-gsap="fade-up"]'));
 */
export const fadeUpStagger = (targets, options = {}) => {
  if (!targets || !targets.length || prefersReducedMotion()) return;

  gsap.from(targets, {
    y: 40,
    opacity: 0,
    duration: options.duration ?? 0.8,
    stagger: options.stagger ?? 0.12,
    ease: options.ease ?? 'power3.out',
    scrollTrigger: {
      trigger: targets[0],
      start: options.start ?? 'top 85%',
      ...options.scrollTrigger,
    },
  });
};

/**
 * Fade in — single element or selector string.
 */
export const fadeIn = (target, options = {}) => {
  if (!target || prefersReducedMotion()) return;

  gsap.from(target, {
    opacity: 0,
    duration: options.duration ?? 0.6,
    delay: options.delay ?? 0,
    ease: 'power2.out',
    scrollTrigger: target.scrollTrigger
      ? undefined
      : {
          trigger: target,
          start: 'top 88%',
          ...options.scrollTrigger,
        },
  });
};

/**
 * Horizontal slide — direction: 'left' | 'right'
 */
export const slideIn = (target, direction = 'left', options = {}) => {
  if (!target || prefersReducedMotion()) return;

  const xOffset = direction === 'left' ? -60 : 60;

  gsap.from(target, {
    x: xOffset,
    opacity: 0,
    duration: options.duration ?? 0.9,
    ease: 'power3.out',
    scrollTrigger: {
      trigger: target,
      start: 'top 85%',
      ...options.scrollTrigger,
    },
  });
};

/**
 * Counter animation — animates a numeric text node from 0 to target value.
 * Usage: animateCounter(el, { target: 500, suffix: '+' });
 */
export const animateCounter = (el, { target = 100, suffix = '', duration = 2 } = {}) => {
  if (!el || prefersReducedMotion()) return;

  const counter = { val: 0 };
  gsap.to(counter, {
    val: target,
    duration,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: el,
      start: 'top 85%',
      once: true,
    },
    onUpdate() {
      el.textContent = `${Math.round(counter.val)}${suffix}`;
    },
  });
};

/**
 * Refresh ScrollTrigger after dynamic content loads (e.g., images, lazy data).
 */
export const refreshScrollTrigger = () => ScrollTrigger.refresh();

export { gsap, ScrollTrigger };
